import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useClasses, useSyncClass, useGCStatus, useRefetchAfterSync } from "@/api/hooks";
import { useFeatureFlag } from "@/providers/FeatureFlagProvider";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";
import { useGCSyncStore } from "@/stores/gcSync";
import { useNotification } from "@/providers/NotificationProvider";
import { useAnalytics } from "@/providers/AnalyticsProvider";
import { notifications } from "@/strings";
import { useGCTokenExpiration } from "./useGCTokenExpiration";

const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook that automatically syncs ALL stale GC-linked classes.
 *
 * Triggers on:
 * - Hard refresh (page load)
 * - If 5+ minutes have passed since last global sync
 *
 * This should be called once in the AuthenticatedLayout.
 */
export function useGCGlobalAutoSync() {
  const gcIntegrationEnabled = useFeatureFlag("gc_integration");
  const { user } = useAuthStore(useShallow((state) => ({ user: state.user })));
  const { data: gcStatus } = useGCStatus({ enabled: gcIntegrationEnabled && !!user });
  const { data: classesData } = useClasses();
  const { mutateAsync: syncClassMutation } = useSyncClass();
  const { show } = useNotification();
  const { track } = useAnalytics();
  const { isGCTokenExpired, handleGCTokenExpired } = useGCTokenExpiration();
  const { startSyncing, stopSyncing } = useGCSyncStore();
  const location = useLocation();
  const refetchAfterSync = useRefetchAfterSync();

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // User-scoped localStorage key for tracking last global sync time
  // (persists across hard refreshes and tab closures)
  const lastGlobalSyncKey = user?.id ? `gc_last_global_sync_${user.id}` : null;

  // Stable callback for syncing a single class
  // Wrapping in useCallback ensures this function doesn't change identity on every render
  const syncClass = useCallback(
    (
      classId: string,
      staleClassIds: string[],
      countersRef: { syncedCount: number; failedCount: number; totalSubmissions: number; tokenExpired: boolean; syncedClassIds: string[] }
    ) => {
      // Atomic check-and-set in store prevents race conditions
      const syncStarted = startSyncing(classId);
      if (!syncStarted) {
        // Already syncing (either from this component or another)
        return;
      }

      // Uses mutateAsync because mutate's per-call callbacks fire only for the latest call on the
      // observer and never after unmount; stopSyncing must run for every class regardless.
      void syncClassMutation(classId).then(
        (result) => {
          stopSyncing(classId);
          if (!isMountedRef.current) return;

          countersRef.totalSubmissions += result.submissions;
          countersRef.syncedCount++;
          countersRef.syncedClassIds.push(classId);

          // Show notification and batch-refetch after ALL syncs complete
          if (countersRef.syncedCount + countersRef.failedCount === staleClassIds.length) {
            if (lastGlobalSyncKey) {
              localStorage.setItem(lastGlobalSyncKey, Date.now().toString());
            }
            // Batch-refetch all synced classes at once
            void refetchAfterSync(countersRef.syncedClassIds);
            if (countersRef.totalSubmissions > 0) {
              show(notifications.googleClassroom.syncComplete(countersRef.totalSubmissions), "success");
            }
            track("gc_global_auto_sync_completed", {
              classCount: countersRef.syncedCount,
              totalSubmissions: countersRef.totalSubmissions,
            });
          }
        },
        (error: unknown) => {
          stopSyncing(classId);
          if (!isMountedRef.current) return;

          countersRef.failedCount++;

          if (isGCTokenExpired(error) && !countersRef.tokenExpired) {
            countersRef.tokenExpired = true;
            handleGCTokenExpired("global_auto_sync");
          }

          // Track completion even with failures
          if (countersRef.syncedCount + countersRef.failedCount === staleClassIds.length) {
            // Only mark as synced if at least one class succeeded;
            // otherwise allow retry on next navigation.
            if (lastGlobalSyncKey && countersRef.syncedCount > 0) {
              localStorage.setItem(lastGlobalSyncKey, Date.now().toString());
            }
            // Batch-refetch for whatever succeeded
            if (countersRef.syncedClassIds.length > 0) {
              void refetchAfterSync(countersRef.syncedClassIds);
            }
            if (!countersRef.tokenExpired && countersRef.failedCount > 0) {
              track("gc_global_auto_sync_partial_failure", {
                syncedCount: countersRef.syncedCount,
                failedCount: countersRef.failedCount,
              });
            }
          }
        }
      );
    },
    [syncClassMutation, startSyncing, stopSyncing, show, track, isGCTokenExpired, handleGCTokenExpired, lastGlobalSyncKey, refetchAfterSync]
  );

  useEffect(() => {
    // Reset mounted flag on mount
    isMountedRef.current = true;

    // Skip if feature flag is off
    if (!gcIntegrationEnabled) {
      return;
    }

    // Skip if classes not loaded yet
    if (!classesData?.items) {
      console.log("[GC Auto-Sync] Skipping: classes not loaded yet");
      return;
    }

    // Skip if user is not in GC mode or doesn't have valid tokens
    if (!user?.gcMode || !gcStatus?.has_refresh_token) {
      if (import.meta.env.DEV) {
        console.log("[GC Auto-Sync] Skipping: user.gcMode=", user?.gcMode, "gcStatus.has_refresh_token=", gcStatus?.has_refresh_token);
      }
      return;
    }

    // Check if we've synced globally recently (persisted in localStorage)
    if (lastGlobalSyncKey) {
      const lastGlobalSync = localStorage.getItem(lastGlobalSyncKey);
      if (lastGlobalSync) {
        const lastSyncTime = parseInt(lastGlobalSync, 10);
        const timeSinceLastSync = Date.now() - lastSyncTime;
        if (timeSinceLastSync < STALE_THRESHOLD_MS) {
          console.log("[GC Auto-Sync] Skipping: synced", Math.round(timeSinceLastSync / 1000), "seconds ago (threshold:", STALE_THRESHOLD_MS / 1000, "s)");
          return;
        }
      }
    }

    // Skip if we're already syncing any classes (check global store to prevent race conditions across remounts)
    const globalSyncingCount = useGCSyncStore.getState().syncingClassIds.size;
    if (globalSyncingCount > 0) {
      console.log("[GC Auto-Sync] Skipping: already syncing", globalSyncingCount, "classes");
      return;
    }

    // Find all GC-linked classes that are stale
    const staleThreshold = new Date(Date.now() - STALE_THRESHOLD_MS);
    const staleClasses = classesData.items.filter((cls) => {
      if (!cls.gcCourseId) return false;
      const lastSync = cls.gcLastSyncedAt ? new Date(cls.gcLastSyncedAt) : null;
      return !lastSync || lastSync < staleThreshold;
    });

    console.log("[GC Auto-Sync] Found", staleClasses.length, "stale classes out of", classesData.items.filter(c => c.gcCourseId).length, "GC-linked classes");

    if (staleClasses.length === 0) {
      if (lastGlobalSyncKey) {
        localStorage.setItem(lastGlobalSyncKey, Date.now().toString());
      }
      return;
    }

    // Counters tracked via ref to avoid closure issues
    const countersRef = {
      totalSubmissions: 0,
      syncedCount: 0,
      failedCount: 0,
      tokenExpired: false,
      syncedClassIds: [] as string[],
    };

    const staleClassIds = staleClasses.map((cls) => cls.id);

    // Sync all stale classes (backend handles rate limits gracefully)
    staleClasses.forEach((cls) => syncClass(cls.id, staleClassIds, countersRef));

    // Cleanup function to prevent state updates on unmounted components
    return () => {
      isMountedRef.current = false;
    };
    // location.key changes on every navigation, triggering re-evaluation
    // syncClass is now stable thanks to useCallback
  }, [location.key, gcIntegrationEnabled, classesData, user?.gcMode, gcStatus?.has_refresh_token, syncClass]);
}
